import type { Drop, DropId, Product, ProductState, ProductType, StockState } from '@/types';

export const DROPS: Record<DropId, Drop> = {
  '07Y': {
    id: '07Y',
    code: 'MOCT 07 / IN FREQUENCY',
    title: '07 YEARS',
    subtitle: 'IN FREQUENCY',
    state: 'live',
    release: '2026-04-01',
    items: 8,
    note: 'AVAILABLE UNTIL SOLD OUT',
  },
  TRESOR: {
    id: 'TRESOR',
    code: 'TRESOR × MOCT',
    title: 'TRESOR × MOCT',
    subtitle: 'BERLIN / YEREVAN CAPSULE',
    state: 'archived',
    release: '2024-11-09',
    items: 4,
    note: 'ARCHIVE OPEN',
  },
  CN: {
    id: 'CN',
    code: 'CLUB NIGHT SERIES',
    title: 'CLUB NIGHT SERIES',
    subtitle: 'POSTERS / EVENT CAPSULES',
    state: 'archived',
    release: '2023-07-23',
    items: 6,
    note: 'ARCHIVE OPEN',
  },
  ANTAR: {
    id: 'ANTAR',
    code: 'ANTAR 018',
    title: 'ANTAR 018',
    subtitle: 'EXPERIMENTAL OBJECTS',
    state: 'coming',
    release: '2026-03-07',
    items: 3,
    note: 'COMING SOON',
  },
};

const sz = (
  xs: StockState,
  s: StockState,
  m: StockState,
  l: StockState,
  xl: StockState,
  xxl: StockState = null,
) => ({ XS: xs, S: s, M: m, L: l, XL: xl, XXL: xxl });

export const PRODUCTS: Product[] = [
  {
    id: '07y-tee-blk',
    code: 'M07-T01',
    name: '7Y FREQUENCY TEE',
    color: 'WASHED BLACK',
    type: 'TEE',
    drop: '07Y',
    price: 9500,
    fabric: '220 GSM / GARMENT-DYED COTTON',
    print: 'WATER-BASED / FRONT + BACK',
    fit: 'BOXED / DROPPED SHOULDER',
    care: '30°C INSIDE OUT / NO TUMBLE',
    ship: 'DISPATCH FROM YEREVAN / 5–10 DAYS INTL',
    state: 'live',
    sizes: sz('open', 'open', 'low', 'open', 'open'),
    gallery: ['FRONT', 'BACK', 'PRINT DETAIL', 'FABRIC'],
    desc: 'Garment-dyed heavyweight tee. Front: dot-matrix MOCT. Back: 7Y frequency map.',
  },
  {
    id: '07y-tee-grey',
    code: 'M07-T02',
    name: '7Y FREQUENCY TEE',
    color: 'WASHED GREY',
    type: 'TEE',
    drop: '07Y',
    price: 9500,
    fabric: '220 GSM / GARMENT-DYED COTTON',
    print: 'WATER-BASED / FRONT + BACK',
    fit: 'BOXED / DROPPED SHOULDER',
    care: '30°C INSIDE OUT / NO TUMBLE',
    ship: 'DISPATCH FROM YEREVAN / 5–10 DAYS INTL',
    state: 'low',
    sizes: sz('soldout', 'low', 'low', 'open', 'open'),
    gallery: ['FRONT', 'BACK', 'PRINT DETAIL', 'FABRIC'],
    desc: 'Same construction, washed-grey colourway. Production 80 units.',
  },
  {
    id: '07y-long',
    code: 'M07-L01',
    name: 'FREQUENCY LONGSLEEVE',
    color: 'BLACK',
    type: 'LONGSLEEVE',
    drop: '07Y',
    price: 12500,
    fabric: '240 GSM / RING-SPUN COTTON',
    print: 'CHEST / SLEEVE / BACK PANEL',
    fit: 'REGULAR / TRUE TO SIZE',
    care: '30°C INSIDE OUT / NO TUMBLE',
    ship: 'DISPATCH FROM YEREVAN / 5–10 DAYS INTL',
    state: 'live',
    sizes: sz('open', 'open', 'open', 'open', 'low', 'open'),
    gallery: ['FRONT', 'SLEEVE', 'BACK PANEL', 'FABRIC'],
    desc: 'Three-zone print system. Sleeve hit reads MOCT / TRANSMISSION / 07.',
  },
  {
    id: '07y-dot-tee',
    code: 'M07-T03',
    name: 'DOT SYSTEM TEE',
    color: 'BONE',
    type: 'TEE',
    drop: '07Y',
    price: 9500,
    fabric: '220 GSM / COTTON',
    print: 'BACK PANEL / DOT-MATRIX',
    fit: 'BOXED',
    care: '30°C / NO TUMBLE',
    ship: 'DISPATCH FROM YEREVAN / 5–10 DAYS INTL',
    state: 'live',
    sizes: sz(null, 'open', 'open', 'open', 'open'),
    gallery: ['FRONT', 'BACK', 'PRINT DETAIL', 'FABRIC'],
    desc: 'Bone-coloured tee with full back-panel dot system.',
  },
  {
    id: '07y-hoodie',
    code: 'M07-H01',
    name: '7Y SIGNAL HOODIE',
    color: 'BLACK',
    type: 'HOODIE',
    drop: '07Y',
    price: 22000,
    fabric: '420 GSM / BRUSHED COTTON',
    print: 'CHEST / SLEEVE',
    fit: 'OVERSIZED',
    care: '30°C INSIDE OUT / NO TUMBLE',
    ship: 'DISPATCH FROM YEREVAN / 5–10 DAYS INTL',
    state: 'live',
    sizes: sz(null, 'low', 'open', 'open', 'open', 'open'),
    gallery: ['FRONT', 'BACK', 'HOOD', 'FABRIC'],
    desc: 'Heavyweight 420gsm. Brushed inside. Anniversary chest hit.',
  },
  {
    id: 'cap-arc',
    code: 'M07-C01',
    name: 'ARCHIVE CAP',
    color: 'BLACK',
    type: 'CAP',
    drop: '07Y',
    price: 7500,
    fabric: 'BRUSHED COTTON / METAL CLASP',
    print: 'EMBROIDERED CROSS',
    fit: 'ONE / ADJUSTABLE',
    care: 'SPOT CLEAN',
    ship: 'DISPATCH FROM YEREVAN / 5–10 DAYS INTL',
    state: 'live',
    sizes: { ONE: 'open' },
    gallery: ['FRONT', 'SIDE', 'BACK', 'CLASP'],
    desc: '6-panel cap, embroidered registration cross at front.',
  },
  {
    id: 'poster-07',
    code: 'M07-P01',
    name: 'EVENT POSTER PRINT',
    color: 'OFFSET / B+W',
    type: 'PRINT',
    drop: '07Y',
    price: 2500,
    fabric: 'A1 / 170GSM UNCOATED',
    print: 'OFFSET LITHO',
    fit: 'A1 / 594 × 841 MM',
    care: 'STORE FLAT',
    ship: 'IN TUBE / 7–14 DAYS INTL',
    state: 'live',
    sizes: { A1: 'open' },
    gallery: ['FULL', 'DETAIL', 'STAMP', 'FOLD'],
    desc: 'Anniversary poster, offset litho. Edition of 200, numbered.',
  },
  {
    id: 'pin-freq',
    code: 'M07-O01',
    name: 'FREQUENCY PIN',
    color: 'STEEL',
    type: 'OBJECT',
    drop: '07Y',
    price: 1200,
    fabric: 'STAINLESS / ENAMEL',
    print: 'ETCHED',
    fit: '24 MM',
    care: 'WIPE DRY',
    ship: 'PADDED ENVELOPE / 7–14 DAYS INTL',
    state: 'live',
    sizes: { ONE: 'open' },
    gallery: ['FRONT', 'BACK', 'CLASP', 'SCALE'],
    desc: 'Etched stainless pin, 24mm. Backed in MOCT card.',
  },
  {
    id: 'antar-tee',
    code: 'AN-T01',
    name: 'ANTAR 018 TEE',
    color: 'BLACK',
    type: 'TEE',
    drop: 'ANTAR',
    price: 9500,
    fabric: '220 GSM / COTTON',
    print: 'BACK PANEL',
    fit: 'BOXED',
    care: '30°C / NO TUMBLE',
    ship: '—',
    state: 'coming',
    sizes: sz('open', 'open', 'open', 'open', 'open'),
    gallery: ['FRONT', 'BACK', 'PRINT', 'FABRIC'],
    desc: 'ANTAR 018 capsule object. Drop opens 07.03.2026.',
  },
  {
    id: 'antar-zine',
    code: 'AN-Z01',
    name: 'ANTAR ZINE / VOL 02',
    color: 'B+W / 64PP',
    type: 'PRINT',
    drop: 'ANTAR',
    price: 3500,
    fabric: 'OFFSET / 64PP',
    print: 'BLACK + SILVER',
    fit: '170 × 240 MM',
    care: 'STORE FLAT',
    ship: '—',
    state: 'coming',
    sizes: { ONE: 'open' },
    gallery: ['COVER', 'SPREAD', 'SPREAD', 'BACK'],
    desc: 'ANTAR documentation, second edition.',
  },
  {
    id: 'tresor-tee',
    code: 'TR-T01',
    name: 'TRESOR × MOCT TEE',
    color: 'BLACK',
    type: 'TEE',
    drop: 'TRESOR',
    price: 9500,
    fabric: '220 GSM / COTTON',
    print: 'CHEST / BACK',
    fit: 'BOXED',
    care: '30°C',
    ship: '—',
    state: 'archived',
    sizes: sz('soldout', 'soldout', 'soldout', 'soldout', 'soldout'),
    gallery: ['FRONT', 'BACK', 'DETAIL', 'TAG'],
    desc: 'Berlin / Yerevan capsule. Archive — not for sale.',
  },
  {
    id: 'tresor-poster',
    code: 'TR-P01',
    name: 'TRESOR × MOCT POSTER',
    color: 'B+W',
    type: 'PRINT',
    drop: 'TRESOR',
    price: 2500,
    fabric: 'A1 OFFSET',
    print: 'OFFSET',
    fit: 'A1',
    care: 'STORE FLAT',
    ship: '—',
    state: 'archived',
    sizes: { A1: 'soldout' },
    gallery: ['FULL', 'DETAIL', 'STAMP', 'FOLD'],
    desc: 'Drop 11 — archive.',
  },
];

export const TYPES: readonly (ProductType | 'ALL')[] = [
  'ALL',
  'TEE',
  'LONGSLEEVE',
  'HOODIE',
  'CAP',
  'PRINT',
  'OBJECT',
];

export const STOCK_STATES = ['ALL', 'LIVE', 'LOW', 'SOLD OUT', 'COMING', 'ARCHIVED'] as const;

export function findProduct(id: string): Product | null {
  return PRODUCTS.find((p) => p.id === id) ?? null;
}

export const STATE_LABEL: Record<ProductState, string> = {
  live: 'AVAILABLE',
  low: 'LOW STOCK',
  soldout: 'SOLD OUT',
  coming: 'COMING SOON',
  archived: 'ARCHIVED',
};
